import { useDraftConfirmation } from "./DraftNavigationProvider";
import { useCallback, useEffect, useRef } from "react";

export type BeforeLeave = (() => Promise<void>) & { markSaved?:()=>void; setPending?:(pending:boolean)=>void };
export type RegisterBeforeLeave = (guard: BeforeLeave | null) => void;

export function manualDraftChanged(initial: string | null, current: string | null): boolean {
  return initial !== null && current !== null && initial !== current;
}

export function protectManualDraftUnload(event: Pick<BeforeUnloadEvent, "preventDefault" | "returnValue">, dirty: boolean): void {
  if (!dirty) return;
  event.preventDefault();
  event.returnValue = "";
}

export async function confirmManualDraftDiscard(dirty: boolean, confirm: () => boolean | Promise<boolean>): Promise<void> {
  if (dirty && !(await confirm())) throw new Error("Your unsaved entry is still open. Save it or cancel it before leaving.");
}

/** Manual forms only: opening untouched forms never interrupts navigation. */
export function useManualDraftGuard(draft: unknown | null, key: string | null, register?: RegisterBeforeLeave): BeforeLeave {
  const requestDiscard = useDraftConfirmation();
  const pending = useRef(false);
  const tracking = useRef<{ key: string | null; initial: string | null; current: string | null }>({ key: null, initial: null, current: null });
  const current = draft === null ? null : JSON.stringify(draft);
  if (current === null) tracking.current = { key: null, initial: null, current: null };
  else if (tracking.current.initial === null || tracking.current.key !== key) tracking.current = { key, initial: current, current };
  else tracking.current.current = current;
  const guard: BeforeLeave = useCallback(async () => {
    if(pending.current)throw new Error("Wait for the save to finish. Your entry remains open.");
    const state = tracking.current;
    await confirmManualDraftDiscard(manualDraftChanged(state.initial, state.current), requestDiscard);
    // Project and app routing may both consult the same guard. One explicit
    // discard decision covers this exact draft; another edit becomes dirty.
    state.initial = state.current;
  }, []);
  guard.markSaved = () => { tracking.current.initial = tracking.current.current; };
  guard.setPending = value => { pending.current=value; };
  useEffect(() => { register?.(guard); return () => register?.(null); }, [register, guard]);
  useEffect(() => {
    const unload = (event: BeforeUnloadEvent) => {
      const state = tracking.current;
      protectManualDraftUnload(event, pending.current || manualDraftChanged(state.initial, state.current));
    };
    window.addEventListener("beforeunload", unload);
    return () => window.removeEventListener("beforeunload", unload);
  }, []);
  return guard;
}
